import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    ArrowLeftIcon,
    DocumentTextIcon,
    EyeIcon,
    PencilSquareIcon,
    PlusIcon,
    TrashIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { DocumentViewer } from '@odo/shared-ui';
import api from '../../../../api/config';
import { fetchTimeline, invalidateTimelineEvent } from '../../../../services/timeline';
import { fetchSdiDrafts, deleteSdiDraft } from '../../../../services/clinicalDrafts';
import { fetchCloudFileDownloadUrl } from '../../../../services/patientFiles';
import { useUser } from '../../../../providers/UserProvider';
import { SdiWizard } from './SdiWizard';
import type { ActionRunnerProps, TimelineEvent } from '../types';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';
import { ClinicalDocumentDeleteModal } from '../clinical/ClinicalDocumentDeleteModal';
import { useRefreshAfterDocumentDelete } from '../clinical/useRefreshAfterDocumentDelete';

type View =
    | { kind: 'list' }
    | { kind: 'create'; resumingDraft?: TimelineEvent }
    | { kind: 'edit'; event: TimelineEvent; resumingDraft?: TimelineEvent }
    | { kind: 'preview'; url: string; fileName: string };

function getSdiPatientFileId(event: TimelineEvent): string | null {
    const payload = event.payload;
    if (!payload || typeof payload !== 'object') return null;
    const files = (payload as Record<string, unknown>).files;
    if (!files || typeof files !== 'object') return null;
    const sdiPdf = (files as Record<string, unknown>).sdiPdf;
    if (!sdiPdf || typeof sdiPdf !== 'object') return null;
    return ((sdiPdf as Record<string, unknown>).patientFileId as string) ?? null;
}

function buildFileName(event: TimelineEvent): string {
    const payload = event.payload as Record<string, unknown> | null;
    const formData = payload?.formData as Record<string, unknown> | undefined;
    const name = (formData?.patientName as string) ?? '';
    const date = (formData?.date as string) ?? '';
    const sanitized = name
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^\w.-]/g, '');
    const versionSuffix = event.version > 1 ? `-v${event.version}` : '';
    return `SDI-${sanitized}-${date}${versionSuffix}.pdf`;
}

function formatRelativeTime(isoString: string): string {
    const date = new Date(isoString);
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
    const diffDays = Math.floor(diffHr / 24);
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
}

interface NewSdiDraftModalProps {
    existingDraft: TimelineEvent;
    onResume: () => void;
    onStartFresh: () => void;
    onCancel: () => void;
}

function NewSdiDraftModal({
    existingDraft,
    onResume,
    onStartFresh,
    onCancel
}: NewSdiDraftModalProps) {
    const timeAgo = formatRelativeTime(existingDraft.updatedAt || existingDraft.createdAt);

    useEscapeKey(onCancel, true);
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-[440px] p-6 relative">
                <div className="flex justify-between items-start mb-3">
                    <h3 className="text-base font-semibold text-gray-900">Unsaved draft found</h3>
                    <button
                        onClick={onCancel}
                        className="text-gray-400 hover:text-gray-700 transition-colors"
                        aria-label="Close"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                    You have an unsaved draft from {timeAgo}. Resume it or start fresh?
                </p>
                <div className="flex justify-end items-center gap-3">
                    <button
                        onClick={onStartFresh}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Start fresh
                    </button>
                    <button
                        onClick={onResume}
                        className="px-4 py-2 text-sm font-semibold text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
                    >
                        Resume
                    </button>
                </div>
            </div>
        </div>
    );
}

interface SdiLandingPageProps extends ActionRunnerProps {
    onSubmitted?: () => void;
    inWorkflow?: boolean;
}

function SdiLandingPage({
    patientId,
    patientName,
    orthopulseStatus,
    onBack,
    onDataChange,
    onSubmitted,
    inWorkflow = false
}: SdiLandingPageProps) {
    const { user } = useUser();
    const isAdmin = user?.database.data.isAdmin ?? false;

    const [view, setView] = useState<View>({ kind: 'list' });
    const [events, setEvents] = useState<TimelineEvent[]>([]);
    const [drafts, setDrafts] = useState<TimelineEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [previewingId, setPreviewingId] = useState<string | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<TimelineEvent | null>(null);
    const [deleteReason, setDeleteReason] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [deletingDraftId, setDeletingDraftId] = useState<string | null>(null);
    const [draftActionError, setDraftActionError] = useState<string | null>(null);
    const [newSdiDraftPrompt, setNewSdiDraftPrompt] = useState<TimelineEvent | null>(null);
    const refreshAfterDelete = useRefreshAfterDocumentDelete(patientId);

    const loadEvents = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const [eventsResult, draftsResult] = await Promise.all([
                fetchTimeline(patientId, { type: 'sdi_submission', isLatest: true, limit: 50 }),
                fetchSdiDrafts(patientId)
            ]);
            setEvents(eventsResult.data);
            setDrafts(draftsResult);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to load SDIs';
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [patientId]);

    useEffect(() => {
        loadEvents();
    }, [loadEvents]);

    const sortedEvents = useMemo(
        () =>
            [...events].sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            ),
        [events]
    );

    const editableEventId =
        sortedEvents.length > 0 && sortedEvents[0].status === 'active' ? sortedEvents[0].id : null;

    const handleWizardBack = useCallback(() => {
        setView({ kind: 'list' });
        loadEvents();
        onDataChange?.();
    }, [loadEvents, onDataChange]);

    const handleNewSdi = useCallback(() => {
        const existingNewDraft = drafts.find((d) => !events.some((e) => e.groupId === d.groupId));
        if (existingNewDraft) {
            setNewSdiDraftPrompt(existingNewDraft);
        } else {
            setView({ kind: 'create' });
        }
    }, [drafts, events]);

    const handleResumeNewDraft = useCallback(() => {
        if (!newSdiDraftPrompt) return;
        setNewSdiDraftPrompt(null);
        setView({ kind: 'create', resumingDraft: newSdiDraftPrompt });
    }, [newSdiDraftPrompt]);

    const handleStartFresh = useCallback(async () => {
        const draft = newSdiDraftPrompt;
        setNewSdiDraftPrompt(null);
        if (draft) {
            try {
                await deleteSdiDraft(patientId, draft.id);
                setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
                onDataChange?.();
            } catch (err: unknown) {
                // Surface the failure but still open the blank wizard so the user is
                // not blocked. The original draft remains in the list.
                const message = err instanceof Error ? err.message : 'Failed to delete draft';
                setDraftActionError(message);
            }
        }
        setView({ kind: 'create' });
    }, [newSdiDraftPrompt, patientId, onDataChange]);

    const handleEditEvent = useCallback(
        (event: TimelineEvent) => {
            const existingEditDraft = drafts.find((d) => d.groupId === event.groupId);
            if (existingEditDraft) {
                setView({ kind: 'edit', event, resumingDraft: existingEditDraft });
            } else {
                setView({ kind: 'edit', event });
            }
        },
        [drafts]
    );

    const handleDeleteDraft = useCallback(
        async (draft: TimelineEvent) => {
            setDeletingDraftId(draft.id);
            setDraftActionError(null);
            try {
                await deleteSdiDraft(patientId, draft.id);
                setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
                onDataChange?.();
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : 'Failed to delete draft';
                setDraftActionError(message);
            } finally {
                setDeletingDraftId(null);
            }
        },
        [patientId, onDataChange]
    );

    const handlePreview = useCallback(
        async (event: TimelineEvent) => {
            const fileId = getSdiPatientFileId(event);
            if (!fileId) {
                setPreviewError('No PDF available for this SDI.');
                return;
            }
            setPreviewingId(event.id);
            setPreviewError(null);
            try {
                const url = await fetchCloudFileDownloadUrl(patientId, fileId);
                setView({ kind: 'preview', url, fileName: buildFileName(event) });
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : 'Failed to load preview';
                setPreviewError(message);
            } finally {
                setPreviewingId(null);
            }
        },
        [patientId]
    );

    const handleConfirmDelete = useCallback(async () => {
        if (!confirmDelete) return;
        setIsDeleting(true);
        setDeleteError(null);
        try {
            await invalidateTimelineEvent(patientId, confirmDelete.id, deleteReason || null);
            refreshAfterDelete();
            setConfirmDelete(null);
            setDeleteReason('');
            await loadEvents();
            onDataChange?.();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to delete SDI';
            setDeleteError(message);
        } finally {
            setIsDeleting(false);
        }
    }, [confirmDelete, deleteReason, patientId, loadEvents, onDataChange, refreshAfterDelete]);

    const handleCancelDelete = useCallback(() => {
        setConfirmDelete(null);
        setDeleteReason('');
        setDeleteError(null);
    }, []);

    // The delete confirm is a modal like any other: Escape or the X/Cancel
    // buttons, never a click-out. Held off while the delete is in flight.
    useEscapeKey(handleCancelDelete, confirmDelete !== null && !isDeleting && !newSdiDraftPrompt);

    if (view.kind === 'create') {
        return (
            <SdiWizard
                patientId={patientId}
                patientName={patientName}
                orthopulseStatus={orthopulseStatus}
                onBack={handleWizardBack}
                resumingDraft={view.resumingDraft}
                onSubmitted={onSubmitted}
                inWorkflow={inWorkflow}
            />
        );
    }

    if (view.kind === 'edit') {
        return (
            <SdiWizard
                patientId={patientId}
                patientName={patientName}
                orthopulseStatus={orthopulseStatus}
                onBack={handleWizardBack}
                editingEvent={view.event}
                resumingDraft={view.resumingDraft}
                onSubmitted={onSubmitted}
                inWorkflow={inWorkflow}
            />
        );
    }

    if (view.kind === 'preview') {
        return (
            <div className="flex flex-col flex-1 min-h-0">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
                    <button
                        onClick={() => setView({ kind: 'list' })}
                        className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-sm"
                    >
                        <ArrowLeftIcon className="w-3.5 h-3.5" />
                        Back to SDIs
                    </button>
                    <div className="text-sm font-semibold text-gray-800 truncate max-w-[50%]">
                        {view.fileName}
                    </div>
                    <button
                        onClick={() => setView({ kind: 'list' })}
                        className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        <XMarkIcon className="w-4 h-4 text-gray-500" />
                    </button>
                </div>
                <div className="flex-1 min-h-0 p-5">
                    <DocumentViewer
                        documents={view.url}
                        apiBaseUrl={api.defaults.baseURL ?? ''}
                        showControls
                        className="h-full"
                        getAuthToken={() => localStorage.getItem('odo_login_token')}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {newSdiDraftPrompt && (
                <NewSdiDraftModal
                    existingDraft={newSdiDraftPrompt}
                    onResume={handleResumeNewDraft}
                    onStartFresh={handleStartFresh}
                    onCancel={() => setNewSdiDraftPrompt(null)}
                />
            )}

            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
                {inWorkflow ? (
                    <div className="w-[100px]" /> /* spacer to keep title centered when hosted in a workflow */
                ) : (
                    <button
                        onClick={onBack}
                        className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-sm"
                    >
                        <ArrowLeftIcon className="w-3.5 h-3.5" />
                        Back to actions
                    </button>
                )}
                <div className="text-sm font-semibold text-gray-800">SDI</div>
                <button
                    onClick={handleNewSdi}
                    className="flex items-center gap-1.5 bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors"
                >
                    <PlusIcon className="w-3.5 h-3.5" />
                    New SDI
                </button>
            </div>

            {previewError && (
                <div className="mx-5 mt-3 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    <span>{previewError}</span>
                    <button
                        onClick={() => setPreviewError(null)}
                        className="text-red-500 hover:text-red-700"
                        aria-label="Dismiss preview error"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>
            )}

            {draftActionError && (
                <div className="mx-5 mt-3 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    <span>{draftActionError}</span>
                    <button
                        onClick={() => setDraftActionError(null)}
                        className="text-red-500 hover:text-red-700"
                        aria-label="Dismiss draft error"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>
            )}

            <div className="flex-1 min-h-0 overflow-auto">
                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
                    </div>
                )}

                {error && (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-xs text-red-500">{error}</div>
                    </div>
                )}

                {!loading && !error && sortedEvents.length === 0 && drafts.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
                        <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center">
                            <DocumentTextIcon className="w-6 h-6 text-gray-400" />
                        </div>
                        <div className="text-center">
                            <div className="text-sm font-semibold text-gray-800">No SDIs yet</div>
                            <div className="text-xs text-gray-500 mt-1">
                                Create the first start day instructions document for this patient.
                            </div>
                        </div>
                        <button
                            onClick={handleNewSdi}
                            className="flex items-center gap-1.5 bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                        >
                            <PlusIcon className="w-4 h-4" />
                            New SDI
                        </button>
                    </div>
                )}

                {!loading && !error && (drafts.length > 0 || sortedEvents.length > 0) && (
                    <div className="p-5 flex flex-col gap-2">
                        {drafts.length > 0 && (
                            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                Drafts
                            </div>
                        )}
                        {drafts.map((draft) => {
                            const publishedParent = events.find(
                                (e) =>
                                    e.groupId === draft.groupId &&
                                    e.status === 'active' &&
                                    e.isLatest
                            );
                            const isDeletingThis = deletingDraftId === draft.id;
                            const lastEditedAt = draft.updatedAt || draft.createdAt;
                            return (
                                <div
                                    key={draft.id}
                                    className="flex items-center justify-between px-4 py-3 rounded-xl border border-yellow-200 bg-yellow-50/40 transition-colors"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                                            <DocumentTextIcon className="w-4 h-4 text-yellow-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-gray-800 truncate">
                                                    {publishedParent
                                                        ? buildFileName(publishedParent)
                                                        : 'New SDI draft'}
                                                </span>
                                                <span className="inline-flex items-center text-[10px] font-medium text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded flex-shrink-0">
                                                    Draft
                                                </span>
                                                {publishedParent && (
                                                    <span className="text-[10px] text-gray-400 flex-shrink-0">
                                                        v{publishedParent.version} active
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                Last edited by{' '}
                                                {draft.updatedByUser?.name ??
                                                    draft.createdByUser?.name ??
                                                    'someone'}{' '}
                                                · {formatRelativeTime(lastEditedAt)}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => {
                                                if (publishedParent) {
                                                    setView({
                                                        kind: 'edit',
                                                        event: publishedParent,
                                                        resumingDraft: draft
                                                    });
                                                } else {
                                                    setView({
                                                        kind: 'create',
                                                        resumingDraft: draft
                                                    });
                                                }
                                            }}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                        >
                                            <PencilSquareIcon className="w-3.5 h-3.5" />
                                            Resume
                                        </button>
                                        <button
                                            onClick={() => handleDeleteDraft(draft)}
                                            disabled={isDeletingThis}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            {isDeletingThis ? (
                                                <div className="w-3.5 h-3.5 rounded-full border-2 border-red-400 border-t-transparent animate-spin" />
                                            ) : (
                                                <TrashIcon className="w-3.5 h-3.5" />
                                            )}
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                        {drafts.length > 0 && sortedEvents.length > 0 && (
                            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide pt-2">
                                Submitted
                            </div>
                        )}
                        {sortedEvents.map((event) => {
                            const isEditable = event.id === editableEventId;
                            const hasPdf = !!getSdiPatientFileId(event);
                            const isPreviewing = previewingId === event.id;
                            const fileName = buildFileName(event);
                            const date = new Date(event.createdAt);
                            const formattedDate = date.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                            });
                            const formattedTime = date.toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit'
                            });

                            return (
                                <div
                                    key={event.id}
                                    className="flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 bg-white hover:border-gray-300 transition-colors"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                            <DocumentTextIcon className="w-4 h-4 text-gray-500" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-gray-800 truncate">
                                                    {fileName}
                                                </span>
                                                {event.status === 'invalidated' && (
                                                    <span className="text-[10px] font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded flex-shrink-0">
                                                        Deleted
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                {formattedDate} at {formattedTime}
                                                {event.createdByUser && (
                                                    <> · By {event.createdByUser.name}</>
                                                )}
                                            </div>
                                            {event.status === 'invalidated' &&
                                                event.invalidationReason && (
                                                    <div className="text-[11px] text-gray-400 italic mt-0.5">
                                                        Deleted: {event.invalidationReason}
                                                    </div>
                                                )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handlePreview(event)}
                                            disabled={!hasPdf || isPreviewing}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            {isPreviewing ? (
                                                <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" />
                                            ) : (
                                                <EyeIcon className="w-3.5 h-3.5" />
                                            )}
                                            Preview
                                        </button>
                                        {isEditable && (
                                            <button
                                                onClick={() => handleEditEvent(event)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                            >
                                                <PencilSquareIcon className="w-3.5 h-3.5" />
                                                Edit
                                            </button>
                                        )}
                                        {isAdmin && event.status === 'active' && (
                                            <button
                                                onClick={() => setConfirmDelete(event)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                                            >
                                                <TrashIcon className="w-3.5 h-3.5" />
                                                Delete
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {confirmDelete !== null && (
                <ClinicalDocumentDeleteModal
                    documentLabel="SDI"
                    patientId={patientId}
                    groupId={confirmDelete.groupId}
                    reason={deleteReason}
                    onReasonChange={setDeleteReason}
                    error={deleteError}
                    onDismissError={() => setDeleteError(null)}
                    isDeleting={isDeleting}
                    onCancel={handleCancelDelete}
                    onConfirm={handleConfirmDelete}
                />
            )}
        </div>
    );
}

export { SdiLandingPage };
